import { configurationsService } from './../services/configurationsService';
import { useContext, useEffect, useState, useMemo } from 'react';
import { ProfileContext } from 'contexts/ProfileContext';
import { AvailableFontsType } from 'utils/fonts';
import { configProfile, CustomBrandSettings, DashboardConfig } from 'types/dashboard-config';

const useDashboardConfig = () => {
    const [dashboardConfig, setDashboardConfig] = useState<any>(undefined);
    const { profile, profileIsReady } = useContext(ProfileContext);

    const configProfileType: configProfile = useMemo(() => {
        if (!profileIsReady || !dashboardConfig) {
            return '';
        }
        if (dashboardConfig?.id === profile?.id) {
            return 'userConfig';
        }
        if (dashboardConfig?.id === profile?.company?.id) {
            return 'companyConfig';
        }
        return 'defaultConfig';
    }, [profileIsReady, dashboardConfig]);

    const injectFont = (fontFamily: AvailableFontsType) => {
        const primaryFont = fontFamily.split(',')[0];
        const linkElement = document.createElement('link');
        linkElement.href = `https://fonts.googleapis.com/css2?family=${primaryFont.replace(' ', '+')}&display=swap`;
        linkElement.rel = 'stylesheet';
        document.head.appendChild(linkElement);
    };

    const parseBase64String = (base64String: string) => {
        if (!base64String) return {};
        const [metadata, base64Data] = base64String.split(',');
        const mimeType = metadata.match(/:(.*?);/)?.[1];
        return { mimeType, base64Data };
    };

    const fetchUserConfig = async () => {
        try {
            const response = await configurationsService.getUserDashboardConfig(profile.id, profile.company.id);
            setDashboardConfig(response?.configuration);
            injectFont(response?.configuration?.fontUrl);
        } catch (err: any) {
            // Error 404 indica que no se encontró la configuración ni para el usuario ni para la empresa
            if (err?.code === 404) {
                setDashboardConfig({});
            }
        }
    };

    const fetchCompanyConfig = async () => {
        try {
            const response = await configurationsService.getCompanyDashboardConfig(profile.company.id);

            return response?.configuration;
        } catch (err: any) {
            return null;
        }
    };

    const updateUserConfig = async (customConfig: CustomBrandSettings) => {
        const { mimeType, base64Data } = parseBase64String(customConfig.logoSrc);

        const bodyOptions: DashboardConfig = {
            companyId: profile.company.id,
            userId: profile.id,
            partnerId: profile.vendor.id,
            domain: 'UPDATE',
            context: 'user',
            logoContent: base64Data,
            logoMimeType: mimeType,
            primaryColor: customConfig.primaryColor,
            secondaryColor: customConfig.secondaryColor,
            primaryColorDark: dashboardConfig.primaryColorDark,
            secondaryColorDark: dashboardConfig.secondaryColorDark,
            fontUrl: customConfig.fontFamily
        };

        try {
            await configurationsService.updateUserDashboardConfig(profile.id, bodyOptions);
            fetchUserConfig();
        } catch (err: any) {
            console.error(err);
        }
    };
    const updateCompanyConfig = async (customConfig: CustomBrandSettings) => {
        const { mimeType, base64Data } = parseBase64String(customConfig.logoSrc);
        const bodyOptions: DashboardConfig = {
            companyId: profile.company.id,
            userId: profile.id,
            partnerId: profile.vendor.id,
            domain: 'UPDATE',
            context: 'company',
            logoContent: base64Data,
            logoMimeType: mimeType as string,
            primaryColor: customConfig.primaryColor,
            secondaryColor: customConfig.secondaryColor,
            primaryColorDark: dashboardConfig.primaryColorDark,
            secondaryColorDark: dashboardConfig.secondaryColorDark,
            fontUrl: customConfig.fontFamily
        };

        try {
            await configurationsService.updateCompanyDashboardConfig(profile.company.id, bodyOptions);
            fetchUserConfig();
        } catch (err: any) {
            return err;
        }
    };

    const removeUserConfig = async () => {
        try {
            await configurationsService.deleteUserDashboardConfig(profile.id, profile.company.id);
            fetchUserConfig();
        } catch (err: any) {
            console.error(err);
        }
    };
    const removeCompanyConfig = async () => {
        try {
            await configurationsService.deleteCompanyDashboardConfig(profile.company.id);
            fetchUserConfig();
        } catch (err: any) {
            console.error(err);
        }
    };

    const createUserDashboardConfig = async (customConfig: CustomBrandSettings) => {
        const { mimeType, base64Data } = parseBase64String(customConfig.logoSrc);
        const bodyOptions: DashboardConfig = {
            companyId: profile.company.id,
            userId: profile.id,
            partnerId: profile.vendor.id,
            domain: 'companydomainnn',
            context: 'user',
            logoContent: base64Data,
            logoMimeType: mimeType as string,
            primaryColor: customConfig.primaryColor,
            secondaryColor: customConfig.secondaryColor,
            primaryColorDark: '#000000',
            secondaryColorDark: '#000000',
            fontUrl: customConfig.fontFamily
        };
        try {
            await configurationsService.createDashboardConfig(bodyOptions);
            fetchUserConfig();
        } catch (err: any) {
            console.error(err);
        }
    };

    const createCompanyDashboardConfig = async (customConfig: CustomBrandSettings) => {
        const { mimeType, base64Data } = parseBase64String(customConfig.logoSrc);
        const bodyOptions: DashboardConfig = {
            companyId: profile.company.id,
            partnerId: profile.vendor.id,
            domain: 'companydomainnn',
            context: 'company',
            logoContent: base64Data,
            logoMimeType: mimeType as string,
            primaryColor: customConfig.primaryColor,
            secondaryColor: customConfig.secondaryColor,
            primaryColorDark: '#000000',
            secondaryColorDark: '#000000',
            fontUrl: customConfig.fontFamily
        };
        try {
            await configurationsService.createDashboardConfig(bodyOptions);
            fetchUserConfig();
        } catch (err: any) {
            console.error(err);
        }
    };

    useEffect(() => {
        profileIsReady && fetchUserConfig();
    }, [profileIsReady]);

    return {
        dashboardConfig,
        configProfileType,
        fetchUserConfig,
        fetchCompanyConfig,
        updateUserConfig,
        updateCompanyConfig,
        removeUserConfig,
        removeCompanyConfig,
        createUserDashboardConfig,
        createCompanyDashboardConfig
    };
};

export default useDashboardConfig;
